import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from "@radix-ui/react-accordion";
import { RhDatePicker, RhSelect } from "@rhythm-ui/react";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo } from "react";

import { OverviewStat } from "@/types/job";

import api from "@/services/api";

import { DEFAULT_OPEN_ACCORDIONS, intervalOptions } from "@/constants/jobs";

import InterviewerAnalyticsTable from "./InterviewPerformancetable";
import RejectDropAnalyticsTable from "./RejectDropAnalyticsTable";
import RejectReasonByStageChart from "./RejectReasonByStageChart";
import ReusableStackedStageChart from "./ReusableStackedStageChart";
import {
  buildDateRangeFromJobCreated,
  getDefaultDateRangeFromJobCreated,
} from "./utils/analyticsUtils";
import JobAnalyticsSkeleton, { AccordionHeader } from "./utils/Utility";
import WeekJobPerformanceTable from "./WeekJobPerformanceTable";

const JobStat: React.FC<any> = (jobDetail) => {
  const [selectedInterval, setSelectedInterval] = React.useState(
    intervalOptions[0],
  );
  const [dateRange, setDateRange] = React.useState<string[]>([]);

  const jobId = jobDetail?.jobDetail?.id;
  const jobCreatedAt = jobDetail?.jobDetail?.created_ts;

  const { startDate, endDate } = useMemo(
    () => buildDateRangeFromJobCreated(jobCreatedAt, dateRange),
    [dateRange, jobCreatedAt],
  );
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "JOB_ANALYTICS",
      jobId,
      startDate,
      endDate,
      selectedInterval.value,
    ],
    queryFn: () =>
      api.company
        .getJobAnalytics(jobId!, startDate!, endDate!, selectedInterval.value)
        .then((res) => res.data),
    enabled: Boolean(jobId && startDate && endDate),
  });

  const isPageLoading = isLoading || isFetching;

  useEffect(() => {
    setDateRange(getDefaultDateRangeFromJobCreated(jobCreatedAt));
  }, [jobCreatedAt]);

  const overview: OverviewStat[] = data?.data_overview ?? [];

  const screeningStageData = data?.screening_analytics ?? {
    overall: {},
    [selectedInterval.value]: [],
  };

  const interviewStageData = data?.interview_stage_analytics ?? {
    overall: {},
    [selectedInterval.value]: [],
  };

  const postClientSubmissionData = data?.post_client_submission_analytics ?? {
    overall: {},
    [selectedInterval.value]: [],
  };

  const weeklyPerformanceData = data?.weekly_job_analytics;
  const rejectDropData = data?.reject_drop_analytics ?? [];
  const rejectReasonData = data?.reject_reason_analytics ?? [];
  const interviewerPerformanceData =
    data?.interviewer_performance_analytics ?? [];

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex w-full items-center justify-between">
        <h4>Job Analytics</h4>

        <div className="flex items-center gap-4">
          <RhSelect
            instanceId="intervalOptions"
            options={intervalOptions}
            value={selectedInterval}
            onChange={(val: any) => setSelectedInterval(val)}
            isSearchable={false}
            className="min-w-[120px]"
            isDisabled={isPageLoading}
          />

          <RhDatePicker
            options={{ mode: "range" }}
            className="w-full max-w-[230px]"
            value={dateRange}
            onChange={(value: string[]) => setDateRange(value)}
            disabled={isPageLoading}
          />
        </div>
      </div>

      {isPageLoading ? (
        <JobAnalyticsSkeleton />
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4">
            {overview.map((stat) => (
              <div key={stat.key} className="rounded bg-blue-50 p-4">
                <p className="text-md font-medium text-gray-700">
                  {stat.label}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          <Accordion
            type="multiple"
            defaultValue={DEFAULT_OPEN_ACCORDIONS}
            className="w-full space-y-4"
          >
            <AccordionItem
              value="overall-stats"
              className="rounded bg-slate-100 p-5"
            >
              <AccordionHeader>Overall Stats</AccordionHeader>
              <AccordionContent>
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[900px]">
                    {weeklyPerformanceData && (
                      <WeekJobPerformanceTable data={weeklyPerformanceData} />
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="screening-stages"
              className="rounded bg-slate-100 p-5"
            >
              <AccordionHeader>Screening Stages</AccordionHeader>
              <AccordionContent>
                <ReusableStackedStageChart
                  data={screeningStageData}
                  interval={selectedInterval.value}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="z-interview-stages"
              className="rounded bg-slate-100 p-5"
            >
              <AccordionHeader>Z Interview Stages</AccordionHeader>
              <AccordionContent>
                <ReusableStackedStageChart
                  data={interviewStageData}
                  interval={selectedInterval.value}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="post-submission-stages"
              className="rounded bg-slate-100 p-5"
            >
              <AccordionHeader>Post Submission Stages</AccordionHeader>
              <AccordionContent>
                <ReusableStackedStageChart
                  data={postClientSubmissionData}
                  interval={selectedInterval.value}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="reject-drop-stats"
              className="rounded bg-slate-100 p-5"
            >
              <AccordionHeader>Reject & Drop Stats (R | D)</AccordionHeader>
              <AccordionContent>
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[900px]">
                    {rejectDropData && (
                      <RejectDropAnalyticsTable
                        data={rejectDropData}
                        interval={selectedInterval.value}
                      />
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="reject-reason"
              className="rounded bg-slate-100 p-5"
            >
              <AccordionHeader>Reject Reason</AccordionHeader>
              <AccordionContent>
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[900px]">
                    <RejectReasonByStageChart data={rejectReasonData} />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="interviewer-performance"
              className="rounded bg-slate-100 p-5"
            >
              <AccordionHeader>Interviewer Performance</AccordionHeader>
              <AccordionContent>
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[900px]">
                    <InterviewerAnalyticsTable
                      data={interviewerPerformanceData}
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </>
      )}
    </div>
  );
};

export default JobStat;
